import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { defaultLimit, defaultOffset } from 'config/Api.Config';
import lossCodeCategoryDeleteApi from 'models/claims/lossCodeCategory/delete/Api.LossCodeCategory.Delete';
import LossCodeCategory from 'models/claims/lossCodeCategory/Model.LossCodeCategory';
import lossCodeCategorySaveApi from 'models/claims/lossCodeCategory/save/Api.LossCodeCategory.Save';
import { useMemo } from 'react';
import { lossCodeCategoryIndexRoute } from './LossCodeCategory.Index';

export enum LossCodeCategoryAction {
  EDIT = 'edit',
  DELETE = 'delete',
}
export type VerifyLossCodeCategoryAction = Utils.VerifyExtends<
  Module.Claims.LossCodeCategory.Actions,
  Utils.ValueOf<typeof LossCodeCategoryAction>
>;
export default function useLossCodeCategory() {
  const { permissions } = useAuthStore();
  const pageRouter = usePageRouter<Module.Claims.LossCodeCategory.Params, Module.Claims.LossCodeCategory.Query>({
    route: lossCodeCategoryIndexRoute,
  });

  const canView =
    permissions.CREATE_LOSSCODECATEGORY || permissions.EDIT_LOSSCODECATEGORY || permissions.DELETE_LOSSCODECATEGORY;

  const listApi = useApi({
    action: LossCodeCategory.list,
    default: { limit: defaultLimit, offset: defaultOffset },
    body: pageRouter.query.list,
    callback: (req) => pageRouter.redirect({}, { ...pageRouter.query, list: req }),
    wait: !canView,
  });

  const selected = useMemo(() => {
    return listApi.payload?.data?.find((it) => it.id.toString() === pageRouter.params.id?.toString());
  }, [listApi.payload?.data, pageRouter.params.id]);

  const deleteApi = useApi({
    action: lossCodeCategoryDeleteApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  const saveApi = useApi({
    action: lossCodeCategorySaveApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  return {
    canView,
    pageRouter,
    listApi,
    deleteApi,
    saveApi,
    selected,
    permissions,
    views: {
      [LossCodeCategoryAction.DELETE]:
        pageRouter.params.action === LossCodeCategoryAction.DELETE && selected && permissions.DELETE_LOSSCODECATEGORY,
      [LossCodeCategoryAction.EDIT]:
        pageRouter.params.action === LossCodeCategoryAction.EDIT &&
        ((permissions.CREATE_LOSSCODECATEGORY && !selected) || (permissions.EDIT_LOSSCODECATEGORY && selected)),
    },
  };
}

export function useLossCodeCategoryProvider() {
  return useDataProvider<ReturnType<typeof useLossCodeCategory>>();
}
